import { useEffect, useState } from "react";
import { api, courseImageUrl, type AuthUser, type CourseResource, type Diplomado, type DiplomadoEdition } from "@/lib/api";

const emptyDiplomadoForm = {
  name: "",
  description: "",
  detail_title: "",
  modality: "Presencial",
  target_audience: "",
  highlights_text: "",
  resources_text: "",
  duration: "",
  hours: "",
  category: "Diplomado",
  image_url: "",
  show_on_landing: true,
  active: true,
};

function parseLines(value: string) {
  return value.split("\n").map((line) => line.trim()).filter(Boolean);
}

function parseResources(value: string): CourseResource[] {
  return parseLines(value).map((line) => {
    const [rawName, ...rest] = line.split("|");
    const url = (rest.join("|") || rawName).trim();
    const fallbackName = url.split("/").pop() || "Recurso del diplomado";
    return { name: rest.length ? rawName.trim() || fallbackName : fallbackName, url };
  });
}

function formatResources(resources?: CourseResource[]) {
  return (resources || []).map((resource) => `${resource.name} | ${resource.url}`).join("\n");
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export default function NuevoDiplomadoModal({ isOpen, onClose, onCreated }: Props) {
  const [form, setForm] = useState(emptyDiplomadoForm);
  const [image, setImage] = useState<File | null>(null);
  const [resources, setResources] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const diplomado = await api.createDiplomado({
        name: form.name,
        description: form.description,
        detail_title: form.detail_title,
        modality: form.modality,
        target_audience: form.target_audience,
        highlights: parseLines(form.highlights_text),
        resource_files: parseResources(form.resources_text),
        duration: form.duration,
        hours: parseInt(form.hours, 10) || 0,
        category: form.category,
        image_url: form.image_url,
        show_on_landing: form.show_on_landing,
        active: form.active,
      });
      if (image) await api.uploadDiplomadoImage(diplomado.id, image);
      for (const file of resources) {
        await api.uploadDiplomadoResource(diplomado.id, file);
      }
      onCreated();
      onClose();
      setForm(emptyDiplomadoForm);
      setImage(null);
      setResources([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al crear diplomado");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <DiplomadoModalShell title="Nuevo diplomado" onClose={onClose} wide>
      <DiplomadoForm
        form={form}
        setForm={setForm}
        onImageChange={setImage}
        onResourcesChange={setResources}
        error={error}
        loading={loading}
        submitLabel="Crear diplomado"
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </DiplomadoModalShell>
  );
}

interface EditDiplomadoProps {
  isOpen: boolean;
  onClose: () => void;
  diplomado: Diplomado;
  onSaved: () => void;
}

export function EditarDiplomadoModal({ isOpen, onClose, diplomado, onSaved }: EditDiplomadoProps) {
  const [form, setForm] = useState({
    name: diplomado.name,
    description: diplomado.description,
    detail_title: diplomado.detail_title || "",
    modality: diplomado.modality || "Presencial",
    target_audience: diplomado.target_audience || "",
    highlights_text: (diplomado.highlights || []).join("\n"),
    resources_text: formatResources(diplomado.resource_files),
    duration: diplomado.duration,
    hours: String(diplomado.hours),
    category: diplomado.category,
    image_url: diplomado.image_url || "",
    show_on_landing: diplomado.show_on_landing,
    active: diplomado.active,
  });
  const [image, setImage] = useState<File | null>(null);
  const [resources, setResources] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      name: diplomado.name,
      description: diplomado.description,
      detail_title: diplomado.detail_title || "",
      modality: diplomado.modality || "Presencial",
      target_audience: diplomado.target_audience || "",
      highlights_text: (diplomado.highlights || []).join("\n"),
      resources_text: formatResources(diplomado.resource_files),
      duration: diplomado.duration,
      hours: String(diplomado.hours),
      category: diplomado.category,
      image_url: diplomado.image_url || "",
      show_on_landing: diplomado.show_on_landing,
      active: diplomado.active,
    });
    setImage(null);
    setResources([]);
    setError("");
  }, [isOpen, diplomado]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.updateDiplomado(diplomado.id, {
        name: form.name,
        description: form.description,
        detail_title: form.detail_title,
        modality: form.modality,
        target_audience: form.target_audience,
        highlights: parseLines(form.highlights_text),
        resource_files: parseResources(form.resources_text),
        duration: form.duration,
        hours: parseInt(form.hours, 10) || 0,
        category: form.category,
        image_url: form.image_url,
        show_on_landing: form.show_on_landing,
        active: form.active,
      });
      if (image) await api.uploadDiplomadoImage(diplomado.id, image);
      for (const file of resources) {
        await api.uploadDiplomadoResource(diplomado.id, file);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al guardar");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <DiplomadoModalShell title="Editar diplomado" onClose={onClose} wide>
      <DiplomadoForm
        form={form}
        setForm={setForm}
        onImageChange={setImage}
        onResourcesChange={setResources}
        error={error}
        loading={loading}
        submitLabel="Guardar cambios"
        onSubmit={handleSubmit}
        onClose={onClose}
        showPreview
      />
    </DiplomadoModalShell>
  );
}

function DiplomadoForm({
  form,
  setForm,
  onImageChange,
  onResourcesChange,
  error,
  loading,
  submitLabel,
  onSubmit,
  onClose,
  showPreview = false,
}: {
  form: typeof emptyDiplomadoForm;
  setForm: React.Dispatch<React.SetStateAction<typeof emptyDiplomadoForm>>;
  onImageChange: (file: File | null) => void;
  onResourcesChange: (files: File[]) => void;
  error: string;
  loading: boolean;
  submitLabel: string;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  showPreview?: boolean;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Nombre del diplomado *</label>
        <input className="input-field" placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
      </div>
      <div>
        <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Descripción detallada *</label>
        <textarea className="input-field min-h-[80px]" placeholder="Descripción" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required />
      </div>
      <div>
        <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Título público del detalle</label>
        <input className="input-field" placeholder="Ej. Diplomado certificado en gestión HSE" value={form.detail_title} onChange={(e) => setForm({ ...form, detail_title: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Modalidad</label>
          <select className="input-field" value={form.modality} onChange={(e) => setForm({ ...form, modality: e.target.value })}>
            <option value="Presencial">Presencial</option>
            <option value="Online">Online</option>
            <option value="Híbrido">Híbrido</option>
          </select>
        </div>
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Dirigido a</label>
          <input className="input-field" placeholder="Profesionales, supervisores, líderes..." value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Puntos importantes / destacados</label>
        <textarea className="input-field min-h-[90px]" placeholder="Uno por línea" value={form.highlights_text} onChange={(e) => setForm({ ...form, highlights_text: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Duración (texto) *</label>
          <input className="input-field" placeholder="Ej. 6 semanas" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} required />
        </div>
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Horas totales *</label>
          <input type="number" className="input-field" placeholder="Ej. 120" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required />
        </div>
      </div>
      <div>
        <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Categoría / Industria</label>
        <input className="input-field" placeholder="Categoría" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
      </div>
      <div className="space-y-3">
        <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Imagen del diplomado</label>
        {showPreview && form.image_url && (
          <img src={courseImageUrl(form.image_url)} alt="Preview" className="w-20 h-16 object-cover rounded-lg border border-[var(--color-border)] mb-2" />
        )}
        <input className="input-field" placeholder="URL de la imagen" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
        <input type="file" accept="image/*" onChange={(e) => onImageChange(e.target.files?.[0] || null)} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
      </div>
      <div className="space-y-3">
        <label className="block text-sm text-[var(--color-text-secondary)] mb-1">Recursos descargables</label>
        <textarea className="input-field min-h-[80px]" placeholder="Nombre del recurso | https://..." value={form.resources_text} onChange={(e) => setForm({ ...form, resources_text: e.target.value })} />
        <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.webp,.zip" onChange={(e) => onResourcesChange(Array.from(e.target.files || []))} className="text-xs text-[var(--color-text-secondary)] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-[var(--color-bg-secondary)] file:text-[var(--color-text-primary)] hover:file:bg-[var(--color-bg-tertiary)] cursor-pointer" />
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.show_on_landing} onChange={(e) => setForm({ ...form, show_on_landing: e.target.checked })} />
        Mostrar en landing page
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
        Diplomado activo
      </label>
      {error && <p className="text-sm text-red-400">{error}</p>}
      <ModalActions onClose={onClose} loading={loading} submitLabel={submitLabel} />
    </form>
  );
}

interface EditionProps {
  isOpen: boolean;
  onClose: () => void;
  diplomadoId: string;
  onCreated: () => void;
}

export function NuevaDiplomadoEdicionModal({ isOpen, onClose, diplomadoId, onCreated }: EditionProps) {
  const [instructors, setInstructors] = useState<AuthUser[]>([]);
  const [form, setForm] = useState({
    edition_label: "", start_date: "", end_date: "", location: "",
    instructor_id: "", evaluator_id: "", capacity: "20", price: "", status: "programado",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) api.getInstructors().then(setInstructors).catch(() => {});
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.createDiplomadoEdition({
        diplomado_id: diplomadoId,
        edition_label: form.edition_label,
        start_date: form.start_date,
        end_date: form.end_date,
        location: form.location,
        instructor_id: form.instructor_id || null,
        evaluator_id: form.evaluator_id || null,
        capacity: parseInt(form.capacity, 10),
        price: parseFloat(form.price) || 0,
        status: form.status,
      });
      onCreated();
      onClose();
      setForm({
        edition_label: "", start_date: "", end_date: "", location: "",
        instructor_id: "", evaluator_id: "", capacity: "20", price: "", status: "programado",
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return <EditionFormModal title="Nueva edición de diplomado" form={form} setForm={setForm} instructors={instructors} error={error} loading={loading} submitLabel="Crear edición" onSubmit={handleSubmit} onClose={onClose} />;
}

interface EditEditionProps {
  isOpen: boolean;
  onClose: () => void;
  edition: DiplomadoEdition;
  onSaved: () => void;
}

export function EditarDiplomadoEdicionModal({ isOpen, onClose, edition, onSaved }: EditEditionProps) {
  const [instructors, setInstructors] = useState<AuthUser[]>([]);
  const [form, setForm] = useState({
    edition_label: edition.edition_label,
    start_date: edition.start_date.slice(0, 10),
    end_date: edition.end_date.slice(0, 10),
    location: edition.location,
    instructor_id: edition.instructor_id || "",
    evaluator_id: edition.evaluator_id || "",
    capacity: String(edition.capacity),
    price: String(edition.price),
    status: edition.status,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) api.getInstructors().then(setInstructors).catch(() => {});
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      edition_label: edition.edition_label,
      start_date: edition.start_date.slice(0, 10),
      end_date: edition.end_date.slice(0, 10),
      location: edition.location,
      instructor_id: edition.instructor_id || "",
      evaluator_id: edition.evaluator_id || "",
      capacity: String(edition.capacity),
      price: String(edition.price),
      status: edition.status,
    });
    setError("");
  }, [isOpen, edition]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.updateDiplomadoEdition(edition.id, {
        edition_label: form.edition_label,
        start_date: form.start_date,
        end_date: form.end_date,
        location: form.location,
        instructor_id: form.instructor_id || null,
        evaluator_id: form.evaluator_id || null,
        capacity: parseInt(form.capacity, 10),
        price: parseFloat(form.price) || 0,
        status: form.status,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error");
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return <EditionFormModal title="Editar edición de diplomado" form={form} setForm={setForm} instructors={instructors} error={error} loading={loading} submitLabel="Guardar edición" onSubmit={handleSubmit} onClose={onClose} />;
}

function EditionFormModal({ title, form, setForm, instructors, error, loading, submitLabel, onSubmit, onClose }: {
  title: string;
  form: { edition_label: string; start_date: string; end_date: string; location: string; instructor_id: string; evaluator_id: string; capacity: string; price: string; status: string };
  setForm: React.Dispatch<React.SetStateAction<{ edition_label: string; start_date: string; end_date: string; location: string; instructor_id: string; evaluator_id: string; capacity: string; price: string; status: string }>>;
  instructors: AuthUser[];
  error: string;
  loading: boolean;
  submitLabel: string;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}) {
  const staff = instructors.filter((u) => u.role === "instructor" || u.role === "admin" || u.role === "super_admin");
  const evaluators = instructors.filter((u) => u.role === "evaluador" || u.can_evaluate);

  return (
    <DiplomadoModalShell title={title} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-3">
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Etiqueta de la edición</label>
          <input className="input-field" placeholder="Ej. Cohorte 2026 - Villahermosa" value={form.edition_label} onChange={(e) => setForm({ ...form, edition_label: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Inicio *</label>
            <input type="date" className="input-field" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Fecha de Fin *</label>
            <input type="date" className="input-field" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} required />
          </div>
        </div>
        <div>
          <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Ubicación / Sede *</label>
          <input className="input-field" placeholder="Ubicación" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Instructor asignado</label>
            <select className="input-field" value={form.instructor_id} onChange={(e) => setForm({ ...form, instructor_id: e.target.value })}>
              <option value="">(Sin instructor)</option>
              {staff.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Evaluador asignado</label>
            <select className="input-field" value={form.evaluator_id} onChange={(e) => setForm({ ...form, evaluator_id: e.target.value })}>
              <option value="">(Sin evaluador)</option>
              {evaluators.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Capacidad máxima</label>
            <input type="number" className="input-field" placeholder="Ej. 20" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Precio (USD) *</label>
            <input type="number" className="input-field" placeholder="Precio" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
          </div>
          <div>
            <label className="text-xs text-[var(--color-text-muted)] mb-1 block">Estado</label>
            <select className="input-field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="programado">Programado</option>
              <option value="en_curso">En curso</option>
              <option value="completado">Completado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <ModalActions onClose={onClose} loading={loading} submitLabel={submitLabel} />
      </form>
    </DiplomadoModalShell>
  );
}

function DiplomadoModalShell({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className={`relative bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-2xl w-full ${wide ? "max-w-2xl" : "max-w-2xl"} max-h-[90vh] overflow-y-auto shadow-2xl p-6`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">{title}</h2>
          <button onClick={onClose} className="cursor-pointer"><i className="ri-close-line text-xl"></i></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalActions({ onClose, loading, submitLabel }: { onClose: () => void; loading: boolean; submitLabel: string }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onClose} className="btn-secondary cursor-pointer">Cancelar</button>
      <button type="submit" disabled={loading} className="btn-primary cursor-pointer">{loading ? "Guardando..." : submitLabel}</button>
    </div>
  );
}
